import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";

import { fontFamily, radius, space } from "@passfirst/ui";

import { type Colors, useColors } from "../../theme";
import type { EntitlementCheck } from "../types";

interface Props {
  /** 1-based question index — drives the progress fill width. */
  current: number;
  total: number;
  entitlement: EntitlementCheck | null;
  /** Live free-tier remaining count (entitlement.freeTier.remaining minus
   * questions answered this batch), so the badge decrements as you go. */
  liveRemaining?: number;
}

// Thin navy progress track + free-tier badge per the 2026 refresh.
// Replaces "Question N of N" text — visual progress reads as well and
// keeps the appbar quiet.
export function ProgressHeader({
  current,
  total,
  entitlement,
  liveRemaining,
}: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const pct = total > 0 ? (current / total) * 100 : 0;
  return (
    <View style={styles.header}>
      <View
        style={styles.track}
        accessibilityRole="progressbar"
        accessibilityValue={{
          min: 0,
          max: total,
          now: current,
          text: `Question ${current} of ${total}`,
        }}
      >
        <View style={[styles.fill, { width: `${pct}%` }]} />
      </View>
      {entitlement?.tier === "FREE" && entitlement.freeTier ? (
        <Text style={styles.freeTierBadge}>
          {liveRemaining ?? entitlement.freeTier.remaining} of{" "}
          {entitlement.freeTier.limit} free
        </Text>
      ) : null}
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: space[4],
    },
    track: {
      flex: 1,
      height: 6,
      backgroundColor: colors.surfaceMuted,
      borderRadius: radius.pill,
      overflow: "hidden",
      marginRight: space[3],
    },
    fill: {
      height: "100%",
      backgroundColor: colors.progressFill,
      borderRadius: radius.pill,
    },
    // Free-tier badge — navySoft pill, soft-navy text (→ #9FC0F0 on dark),
    // 12/700 per design_handoff_2.
    freeTierBadge: {
      fontFamily: fontFamily.bold,
      fontSize: 12,
      color: colors.accentSoftText,
      backgroundColor: colors.surfacePicked,
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: radius.pill,
      overflow: "hidden",
    },
  });
}
